import { useEffect, useRef, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import styles from './DebtCard.module.scss';
import OutlinedFormInput from '../../pages/Finance/components/OutlinedFormInput/OutlinedFormInput';
import MeritoDateField from '../../pages/Finance/components/DateField/MeritoDateField';
import { DebtCardData, debtErrorMessage, debtType } from '../../interfaces/interfaces';
import { DebtApi, DebtDetailsInput } from './debtApi';
import { START_DATE_FORMAT, isCompleteDate, isDraftDebt } from './debtHelpers';

// how long after the last keystroke the balance and start date save themselves
const DETAILS_SAVE_DELAY_MS = 800;

type DetailField = 'outstanding' | 'startDate';
type DetailFieldState = {
    focused: boolean;
    pending: number;
    latestSeq: number;
    lastSent: number | string | undefined;
    timer: ReturnType<typeof setTimeout> | undefined;
};

type Props = {
    debt: DebtCardData;
    api: DebtApi;
    // takes the server's copy of the debt after a save
    onChange: (debt: DebtCardData) => void;
    // the parent form was submitted with errors: show missing details even on
    // fields the user never touched, like the company details inputs do
    showErrors: boolean;
    // the card's error line: cleared on a save, set when a save fails
    onErrorMessage: (message: string) => void;
};

// The outstanding balance and (term loans only) start date. Only mounted once
// OCR has read the sanction letter. Local input state, so the form keeps the
// server's copy of the debt; the inputs save themselves 800ms after typing
// stops (so a refresh doesn't lose the value) and straight away on blur.
const DebtDetailsFields = ({ debt, api, onChange, showErrors, onErrorMessage }: Props) => {
    const [outstanding, setOutstanding] = useState('');
    const [startDate, setStartDate] = useState<Dayjs | null>(null);
    // errors show only after the user leaves a field, like the company details inputs
    const [outstandingTouched, setOutstandingTouched] = useState(false);
    const [startDateTouched, setStartDateTouched] = useState(false);

    const outstandingError = outstanding === '' ? 'Enter the outstanding balance on this loan' : null;
    const startDateError = !isCompleteDate(startDate) ? 'Enter the actual start date of this loan' : null;

    // Per-field bookkeeping for the saves, in refs so timers and async replies
    // always read the latest values:
    //  focused: the user is in the field, so server values mustn't overwrite it
    //  pending: saves queued or in flight; their replies are about to change the value
    //  latestSeq: id of the newest save; replies from older ones are ignored
    //  lastSent: newest value sent, so the same value isn't sent twice
    //  timer: the typing-stopped save waiting to fire
    const detailFields = useRef<Record<DetailField, DetailFieldState>>({
        outstanding: { focused: false, pending: 0, latestSeq: 0, lastSent: undefined, timer: undefined },
        startDate: { focused: false, pending: 0, latestSeq: 0, lastSent: undefined, timer: undefined },
    });
    // one save at a time per card, in the order they were made, so the server
    // can't end up storing an older value that happened to arrive last
    const saveQueue = useRef<Promise<void>>(Promise.resolve());

    // a field can't take a server value while it's being edited or has saves pending
    const isFieldLocked = (field: DetailField) =>
        detailFields.current[field].focused || detailFields.current[field].pending > 0;

    useEffect(() => {
        if (isFieldLocked('outstanding')) return;
        setOutstanding(debt?.currentOutstanding != null ? String(debt.currentOutstanding) : '');
    }, [debt?.currentOutstanding]);

    useEffect(() => {
        if (isFieldLocked('startDate')) return;
        // YYYY-MM-DD is ISO, which dayjs parses natively
        setStartDate(debt?.actualStartDate ? dayjs(debt.actualStartDate) : null);
    }, [debt?.actualStartDate]);

    // don't fire a pending typing-stopped save after the fields are gone
    useEffect(() => () => {
        clearTimeout(detailFields.current.outstanding.timer);
        clearTimeout(detailFields.current.startDate.timer);
    }, []);

    const queueSave = (field: DetailField, details: DebtDetailsInput) => {
        // only mounted once a letter is processed, so this never runs on a draft
        if (isDraftDebt(debt)) return;
        const { debtId } = debt;
        const state = detailFields.current[field];
        const seq = ++state.latestSeq;
        state.pending += 1;
        saveQueue.current = saveQueue.current.then(async () => {
            try {
                const updatedDebt = await api.updateDebtDetails(debtId, details);
                // only the newest save's reply is applied; older ones are already out of date
                if (seq === state.latestSeq) {
                    onErrorMessage('');
                    onChange(updatedDebt);
                }
            } catch {
                if (seq === state.latestSeq) {
                    // forget the failed value so the next blur or pause retries it
                    state.lastSent = undefined;
                    onErrorMessage(debtErrorMessage.detailsSave);
                }
            } finally {
                state.pending -= 1;
            }
        });
    };

    // the value the server has, or is about to have once queued saves land
    const savedOutstanding = () => {
        const { lastSent } = detailFields.current.outstanding;
        return lastSent !== undefined ? (lastSent as number) : debt?.currentOutstanding ?? null;
    };
    const savedStartDate = () => {
        const { lastSent } = detailFields.current.startDate;
        return lastSent !== undefined ? (lastSent as string) : debt?.actualStartDate ?? null;
    };

    const commitOutstanding = (text: string) => {
        if (text === '') return; // the backend can't clear it, so an empty value is never sent
        const value = Number(text);
        if (!Number.isFinite(value) || value === savedOutstanding()) return;
        detailFields.current.outstanding.lastSent = value;
        queueSave('outstanding', { currentOutstanding: value });
    };

    const commitStartDate = (date: Dayjs | null) => {
        if (!isCompleteDate(date)) return;
        const value = date.format(START_DATE_FORMAT);
        if (value === savedStartDate()) return;
        detailFields.current.startDate.lastSent = value;
        queueSave('startDate', { actualStartDate: value });
    };

    const handleOutstandingChange = (text: string) => {
        setOutstanding(text);
        const state = detailFields.current.outstanding;
        clearTimeout(state.timer);
        state.timer = setTimeout(() => commitOutstanding(text), DETAILS_SAVE_DELAY_MS);
    };

    const handleStartDateChange = (date: Dayjs | null) => {
        setStartDate(date);
        const state = detailFields.current.startDate;
        clearTimeout(state.timer);
        state.timer = setTimeout(() => commitStartDate(date), DETAILS_SAVE_DELAY_MS);
    };

    const handleOutstandingBlur = () => {
        const state = detailFields.current.outstanding;
        state.focused = false;
        clearTimeout(state.timer);
        setOutstandingTouched(true);
        if (outstanding === '') {
            // can't be cleared on the server, so show what it actually holds;
            // the error stays only when nothing was ever saved, matching validation
            const saved = savedOutstanding();
            setOutstanding(saved != null ? String(saved) : '');
            return;
        }
        commitOutstanding(outstanding);
    };

    const handleStartDateBlur = () => {
        const state = detailFields.current.startDate;
        state.focused = false;
        clearTimeout(state.timer);
        setStartDateTouched(true);
        if (!isCompleteDate(startDate)) {
            // a half-typed date is never saved, so go back to the saved one
            const saved = savedStartDate();
            setStartDate(saved ? dayjs(saved) : null);
            return;
        }
        commitStartDate(startDate);
    };

    return (
        <div className={styles.detailsRow}>
            <div
                className={styles.detailsField}
                // focus bubbles up from the input; while here, server values wait
                onFocus={() => { detailFields.current.outstanding.focused = true; }}
            >
                <OutlinedFormInput
                    name="currentOutstanding"
                    label="Current outstanding balance"
                    placeholder="10,000"
                    fieldType="lg"
                    isCurrency={true}
                    unitPlaceHolder="₹"
                    value={outstanding}
                    onChange={(e) => handleOutstandingChange(e.target.value ?? '')}
                    onBlur={handleOutstandingBlur}
                    error={outstandingError}
                    touched={outstandingTouched || showErrors}
                />
            </div>
            {/* the backend only accepts a start date for term debt */}
            {debt?.debtType === debtType.term ? (
                <div
                    className={styles.detailsField}
                    onFocus={() => { detailFields.current.startDate.focused = true; }}
                >
                    <MeritoDateField
                        name="actualStartDate"
                        label="Actual start date"
                        format="DD/MM/YYYY"
                        size="md2"
                        showIcon={false}
                        value={startDate}
                        onChange={(e) => handleStartDateChange(e.target.value)}
                        onBlur={handleStartDateBlur}
                        error={startDateError}
                        touched={startDateTouched || showErrors}
                    />
                </div>
            ) : null}
        </div>
    );
};

export default DebtDetailsFields;
